import SubmissionBasicDetailPanel from "./SubmissionBasicDetailPanel";
import {useEffect, useState} from "react";
import {SubmissionDto} from "../../client/submission/SubmissionApiClient";
import {useClientLocator} from "../../client/ApiClientLocator";
import {useSnackbar} from "@dain/mui-components"
import {useTranslation} from "react-i18next";
import {CommonTranslKey} from "../../common/CommonTranslKey";
import {SubmissionTranslKey} from "./SubmissionTranslKey";
import SubmissionDocumentsPanel from "./document/SubmissionDocumentsPanel";
import SubmissionStateHistoryPanel from "./state/SubmissionStateHistoryPanel";
import Skeleton from "../../common/skeleton/Skeleton";
import Modal from "../../common/modal/Modal";
import TabNav from "../../common/tab/TabNav";
import SubmissionDeliveryPanel from "./delivery/SubmissionDeliveryPanel";
import {useUserRoles} from "../user/UserContext";
import UserRoleResolver from "../user/UserRoleResolver";
import PersonAnonymizer from "../person/PersonAnonymizer";

export interface SubmissionDetailDialogProps {
    open: boolean;
    onClose: (updated: boolean) => void;
    submissionId: number | undefined;
    anonymizePersonNames?: boolean;
}

export default function SubmissionDetailDialog(props: Readonly<SubmissionDetailDialogProps>) {
    const {open, onClose, submissionId, anonymizePersonNames} = props;

    const {submissionClient} = useClientLocator();
    const snackbar = useSnackbar();
    const {t} = useTranslation();

    const [submission, setSubmission] = useState<SubmissionDto>();
    const [refresh] = useState(Date.now());
    const [updated, setUpdated] = useState(false);

    const roles = useUserRoles();
    const isAdministrator = UserRoleResolver.isAdministrator(roles);
    const isAtLeastOperator = UserRoleResolver.isAtLeastOperator(roles);

    useEffect(() => {
        if (open && submissionId) {
            submissionClient.findById(submissionId)
                .then((submission) => {
                    if (anonymizePersonNames) {
                        if (submission?.submittedBy?.person) {
                            submission.submittedBy.person = PersonAnonymizer.anonymizePerson(submission.submittedBy.person);
                        }
                    }
                    setSubmission(submission);
                })
                .catch((error) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error));
        }
        if (!open) {
            setSubmission(undefined);
            setUpdated(false);
        }
    }, [open, submissionId, anonymizePersonNames, submissionClient, snackbar, refresh, t]);

    let content: React.JSX.Element;
    if (submission) {
        content = (
            <SubmissionBasicDetailPanel submission={submission}/>
        );
    } else {
        content = (
            <Skeleton count={3}/>
        );
    }

    const basicDetailPanelTabItem = {
        label: t(SubmissionTranslKey.DETAILS),
        content: content
    };
    const documentPanelTabItem = {
        label: t(SubmissionTranslKey.DOCUMENTS),
        content: <SubmissionDocumentsPanel submission={submission} />
    };
    const historyPanelTabItem = {
        label: t(SubmissionTranslKey.HISTORY),
        content: <SubmissionStateHistoryPanel submission={submission} anonymizePersonNames={anonymizePersonNames} />
    };
    const submissionDeliveryPanelTabItem = {
        label: t(SubmissionTranslKey.SUBMISSION_DELIVERY),
        content: <SubmissionDeliveryPanel submission={submission} />
    };

    let tabItems;
    if (isAdministrator) {
        tabItems = [basicDetailPanelTabItem, documentPanelTabItem, submissionDeliveryPanelTabItem, historyPanelTabItem];
    } else if (isAtLeastOperator) {
        tabItems = [basicDetailPanelTabItem, submissionDeliveryPanelTabItem, historyPanelTabItem];
    } else {
        tabItems = [basicDetailPanelTabItem, historyPanelTabItem];
    }

    const iconProps = {
        type: "complex",
        name: "doc-filled",
    };

    return (
        <Modal open={open}
               label={t(SubmissionTranslKey.RECEIVED_DOCUMENT)}
               iconProps={iconProps}
               onClose={() => onClose(updated)}
               maxWidth={"75rem"} >
            <TabNav items={tabItems} />
        </Modal>
    );
}
